<script setup>
import { computed, reactive, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import { useLocalStorage } from "@vueuse/core";
import BrandModalogShell from "@/Components/Modalogs/BrandModalogShell.vue";
import BrandThrobber from "@/Components/Brand/BrandThrobber.vue";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { useModalogStore } from "@/stores/modalog.js";
import { useToastStore } from "@/stores/toast.js";

/**
 * Redesigned "Create a List" modalog (GH-848), extended with an edit mode
 * (GH-920): editing hides the type tabs and adds Event Date + Event
 * Description per Figma, saving through giftlist.update.
 */
const props = defineProps({
    listType: {
        type: String,
        default: "wish",
    },
    isEdit: {
        type: Boolean,
        default: false,
    },
    // Required when isEdit; prefills the form and receives the update.
    list: {
        type: Object,
        default: null,
    },
});

const modalogStore = useModalogStore();
const toastStore = useToastStore();
const authUser = useAuthUser();

// Gift Hero experts open on the Expert tab, but can still switch to a
// regular wish list (BH-67).
const isGiftHero = computed(() => !!authUser.value?.is_gift_hero);

// event_date arrives as "YYYY-MM-DD"; split feeds the three selects.
const savedEventDate = computed(() =>
    props.list?.event_date
        ? props.list.event_date.split("-").map(Number)
        : [null, null, null],
);

const form = reactive({
    name: "",
    privacy: "public",
    type: "wish",
    description: "",
});

// --- Event date (edit mode only) ---
const eventMonth = ref(null);
const eventDay = ref(null);
const eventYear = ref(null);

// The modalog host keeps this component mounted across closes (stable-root
// pattern) and reuses the instance across opens, so setup-time state leaks
// between runs — e.g. create-mode defaults showing in the edit form right
// after a list is created (GH-955). Everything per-run seeds here, at setup
// and again on every open.
function seedFromProps() {
    const [year, month, day] = savedEventDate.value;

    form.name = props.isEdit ? props.list.name : "";
    form.privacy = props.isEdit ? props.list.privacy : "public";
    form.type = props.isEdit
        ? props.list.type
        : isGiftHero.value
          ? "expert"
          : props.listType;
    form.description = props.isEdit ? (props.list.description ?? "") : "";

    eventMonth.value = month;
    eventDay.value = day;
    eventYear.value = year;

    errors.value = {};
    privacyOpen.value = false;
}

watch(
    () => modalogStore.hasModalog,
    (isOpen) => {
        if (isOpen) {
            seedFromProps();
        }
    },
);

const monthOptions = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
].map((label, index) => ({ value: index + 1, label }));

const currentYear = new Date().getFullYear();
const yearOptions = computed(() => {
    const years = new Set(
        Array.from({ length: 6 }, (_, offset) => currentYear + offset),
    );

    // Keep an already-saved out-of-range year selectable.
    if (savedEventDate.value[0]) {
        years.add(savedEventDate.value[0]);
    }

    return [...years].sort();
});

const daysInSelectedMonth = computed(() =>
    eventMonth.value
        ? new Date(
              eventYear.value ?? currentYear,
              eventMonth.value,
              0,
          ).getDate()
        : 31,
);

const dayOptions = computed(() =>
    Array.from({ length: daysInSelectedMonth.value }, (_, index) => index + 1),
);

// A shorter month can strand a saved day (e.g. the 31st → June).
watch(daysInSelectedMonth, (days) => {
    if (eventDay.value > days) {
        eventDay.value = null;
    }
});

const isEventDateComplete = computed(
    () => !!(eventMonth.value && eventDay.value && eventYear.value),
);

const isEventDateEmpty = computed(
    () => !eventMonth.value && !eventDay.value && !eventYear.value,
);

const eventDateValue = computed(() => {
    if (!isEventDateComplete.value) {
        return null;
    }

    const pad = (part) => String(part).padStart(2, "0");

    return `${eventYear.value}-${pad(eventMonth.value)}-${pad(eventDay.value)}`;
});

function clearEventDate() {
    eventMonth.value = null;
    eventDay.value = null;
    eventYear.value = null;
}

const errors = ref({});
const saving = ref(false);

const isExpert = computed(() => form.type === "expert");
const listMoniker = computed(() => (isExpert.value ? "Expert" : "Wish"));

// --- "Hey There, Expert" callout. Dismissal persists across sessions; a
//     one-line "What are Expert Lists?" link stands in afterwards to bring
//     it back.
const expertCalloutDismissed = useLocalStorage(
    "expertListCalloutDismissed",
    false,
    { listenToStorageChanges: true },
);

// --- Privacy dropdown
const privacyOptions = [
    {
        value: "public",
        label: "Public",
        description: "Anyone online can view it",
    },
    {
        value: "private",
        label: "Private Link",
        description: "Only those with a link can view it",
    },
];

const privacyOpen = ref(false);
const selectedPrivacy = computed(() =>
    privacyOptions.find((option) => option.value === form.privacy),
);

// First run: everything seedFromProps touches is declared by now.
seedFromProps();

function selectPrivacy(option) {
    form.privacy = option.value;
    privacyOpen.value = false;
}

const canSubmit = computed(
    () =>
        form.name.trim().length > 0 &&
        (!props.isEdit || isEventDateComplete.value || isEventDateEmpty.value),
);

const errorFor = (field) => errors.value[field]?.[0];

function handleSubmitError(error) {
    if (error?.response?.status === 422) {
        errors.value = error.response.data?.errors ?? error.response.data ?? {};
    } else {
        errors.value = {
            general: ["Something went wrong — please try again."],
        };
    }
}

async function createList() {
    if (!canSubmit.value || saving.value) {
        return;
    }

    saving.value = true;
    errors.value = {};

    try {
        const { data } = await axios.post(route("giftlist.store"), {
            name: form.name,
            privacy: form.privacy,
            type: form.type,
        });

        modalogStore.closeModalog();
        toastStore.success(`"${form.name}" was created.`);

        // Refresh list props for back-navigation, then land on the new list.
        router.reload({
            only: ["giftlists", "expertlists"],
            onFinish: () => {
                router.get(`/list/${data.uuid}`);
            },
        });
    } catch (error) {
        handleSubmitError(error);
    } finally {
        saving.value = false;
    }
}

async function saveList() {
    if (!canSubmit.value || saving.value) {
        return;
    }

    saving.value = true;
    errors.value = {};

    try {
        await axios.put(
            route("giftlist.update", { giftlist: props.list.uuid }),
            {
                ...form,
                event_date: eventDateValue.value,
            },
        );

        modalogStore.closeModalog();
        toastStore.success("List details saved.");

        // Refresh the page props so the masthead reflects the edits.
        router.reload();
    } catch (error) {
        handleSubmitError(error);
    } finally {
        saving.value = false;
    }
}

function submit() {
    return props.isEdit ? saveList() : createList();
}

const inputClasses =
    "h-11 w-full rounded-[5px] border border-solid border-[#a79e9b] bg-white px-4 font-brand text-[15px] text-brand-navy placeholder:text-[#3c3938]/50 focus:border-brand-navy focus:outline-none";
const labelClasses = "mb-1.5 block text-[15px] font-bold text-[#3c3938]";
const eventSelectClasses =
    "h-11 w-full appearance-none rounded-[5px] border border-solid border-[#cdc4c2] bg-white bg-none py-0 pl-4 pr-9 font-brand text-[15px] text-[#1d2333] focus:border-brand-navy focus:outline-none";
const eventCaretClasses =
    "pointer-events-none absolute right-3.5 top-1/2 h-2.5 w-[13px] -translate-y-1/2 text-brand-red";
</script>

<template>
    <!-- Stable root; the shell is gated on hasModalog (see BrandModalogShell). -->
    <div>
        <BrandModalogShell
            v-if="modalogStore.hasModalog"
            :title="isEdit ? `Edit ${listMoniker} List` : 'Create a List'"
        >
            <!-- Segmented control -->
            <div
                v-if="!isEdit"
                class="flex h-14 items-stretch gap-1.5 rounded-[8px] border border-solid border-[#e6dfdd] p-1.5"
                role="tablist"
            >
                <button
                    type="button"
                    role="tab"
                    class="flex-1 rounded-[5px] text-[15px] font-bold transition"
                    :class="
                        !isExpert
                            ? 'bg-brand-navy text-white'
                            : 'text-brand-navy hover:bg-brand-navy/5'
                    "
                    :aria-selected="!isExpert"
                    @click="form.type = 'wish'"
                >
                    New Wish List
                </button>
                <button
                    type="button"
                    role="tab"
                    class="flex flex-1 items-center justify-center gap-2 rounded-[5px] text-[15px] font-bold transition"
                    :class="
                        isExpert
                            ? 'bg-brand-navy text-white'
                            : 'text-brand-navy hover:bg-brand-navy/5'
                    "
                    :aria-selected="isExpert"
                    @click="form.type = 'expert'"
                >
                    <svg
                        viewBox="0 0 19 23"
                        class="h-[22px] w-[18px]"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path
                            d="M9.08823 0L0 4.01299V10.0325C0 15.6005 3.87765 20.8073 9.08823 22.0714C14.2988 20.8073 18.1765 15.6005 18.1765 10.0325V4.01299L9.08823 0Z"
                            fill="url(#ghShieldCreateListTab)"
                        />
                        <path
                            d="M13.9272 6.49583L10.2154 7.20229C10.0691 7.22851 9.96183 7.36463 9.96183 7.52409V10.6053C9.92691 10.6146 9.89128 10.624 9.8539 10.6322C9.63167 10.6825 9.36889 10.713 9.08811 10.713C8.83309 10.713 8.59252 10.6879 8.38406 10.6455C8.32409 10.6333 8.26766 10.6193 8.21369 10.6049V7.52445C8.21369 7.36499 8.10646 7.22887 7.96008 7.20265L4.24898 6.49619C4.06344 6.46279 3.89484 6.61615 3.89484 6.81799V14.5466C3.89484 14.7061 4.00207 14.8422 4.14845 14.8684L7.85955 15.5749C8.04508 15.6083 8.21369 15.4549 8.21369 15.2531V12.5508C8.4927 12.5961 8.78547 12.6194 9.08811 12.6198C9.3904 12.6198 9.68282 12.5957 9.96148 12.5508V15.2535C9.96148 15.4553 10.1304 15.6087 10.316 15.5753L14.0278 14.8688C14.1742 14.8426 14.2814 14.7065 14.2814 14.547V6.81835C14.2814 6.61651 14.1124 6.46315 13.9269 6.49655L13.9272 6.49583Z"
                            fill="#fff"
                        />
                        <defs>
                            <linearGradient
                                id="ghShieldCreateListTab"
                                x1="6.4916"
                                y1="1.83929"
                                x2="17.1247"
                                y2="25.6074"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop stop-color="#FFB545" />
                                <stop offset="1" stop-color="#DB7712" />
                            </linearGradient>
                        </defs>
                    </svg>
                    New Expert List
                </button>
            </div>

            <!-- "Hey There, Expert" callout -->
            <div
                v-if="!isEdit && isExpert && !expertCalloutDismissed"
                class="relative mt-6 flex items-center gap-6 rounded-[5px] p-6"
                :style="{
                    background:
                        'radial-gradient(circle 350px at 50px 55px, #ffb545 0%, #fc8c42 25%, #f9643f 50%, #f53c3c 75%, #f4283b 87.5%, #f21439 100%)',
                }"
            >
                <svg
                    viewBox="0 0 19 23"
                    class="h-[68px] w-[56px] shrink-0"
                    fill="none"
                    aria-hidden="true"
                >
                    <path
                        d="M9.08823 0L0 4.01299V10.0325C0 15.6005 3.87765 20.8073 9.08823 22.0714C14.2988 20.8073 18.1765 15.6005 18.1765 10.0325V4.01299L9.08823 0Z"
                        fill="url(#ghShieldCreateListCallout)"
                    />
                    <path
                        d="M13.9272 6.49583L10.2154 7.20229C10.0691 7.22851 9.96183 7.36463 9.96183 7.52409V10.6053C9.92691 10.6146 9.89128 10.624 9.8539 10.6322C9.63167 10.6825 9.36889 10.713 9.08811 10.713C8.83309 10.713 8.59252 10.6879 8.38406 10.6455C8.32409 10.6333 8.26766 10.6193 8.21369 10.6049V7.52445C8.21369 7.36499 8.10646 7.22887 7.96008 7.20265L4.24898 6.49619C4.06344 6.46279 3.89484 6.61615 3.89484 6.81799V14.5466C3.89484 14.7061 4.00207 14.8422 4.14845 14.8684L7.85955 15.5749C8.04508 15.6083 8.21369 15.4549 8.21369 15.2531V12.5508C8.4927 12.5961 8.78547 12.6194 9.08811 12.6198C9.3904 12.6198 9.68282 12.5957 9.96148 12.5508V15.2535C9.96148 15.4553 10.1304 15.6087 10.316 15.5753L14.0278 14.8688C14.1742 14.8426 14.2814 14.7065 14.2814 14.547V6.81835C14.2814 6.61651 14.1124 6.46315 13.9269 6.49655L13.9272 6.49583Z"
                        fill="#fff"
                    />
                    <defs>
                        <linearGradient
                            id="ghShieldCreateListCallout"
                            x1="6.4916"
                            y1="1.83929"
                            x2="17.1247"
                            y2="25.6074"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stop-color="#FFB545" />
                            <stop offset="1" stop-color="#DB7712" />
                        </linearGradient>
                    </defs>
                </svg>
                <div class="min-w-0">
                    <h3
                        class="font-brand-display text-[17px] font-black italic uppercase leading-[1.05] text-white"
                    >
                        Hey There, Expert.
                    </h3>
                    <p
                        class="mt-1.5 text-[12px] font-semibold leading-[15px] text-white"
                    >
                        Are you an influencer or category expert? Are you a
                        brand that just wants to show off their products? Expert
                        Lists are for you.
                    </p>
                </div>
                <button
                    type="button"
                    class="absolute right-3 top-3 grid size-5 place-items-center text-white transition hover:opacity-75"
                    aria-label="Dismiss"
                    @click="expertCalloutDismissed = true"
                >
                    <svg
                        viewBox="0 0 8 8"
                        class="size-2"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        aria-hidden="true"
                    >
                        <path d="M1 1l6 6M7 1L1 7" />
                    </svg>
                </button>
            </div>

            <!-- Dismissed: a one-line way back to the callout -->
            <button
                v-else-if="!isEdit && isExpert && expertCalloutDismissed"
                type="button"
                class="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold text-[#3c3938] transition hover:text-brand-navy"
                @click="expertCalloutDismissed = false"
            >
                <svg
                    viewBox="0 0 19 23"
                    class="h-[17px] w-[14px]"
                    fill="none"
                    aria-hidden="true"
                >
                    <path
                        d="M9.08823 0L0 4.01299V10.0325C0 15.6005 3.87765 20.8073 9.08823 22.0714C14.2988 20.8073 18.1765 15.6005 18.1765 10.0325V4.01299L9.08823 0Z"
                        fill="url(#ghShieldCreateListLink)"
                    />
                    <path
                        d="M13.9272 6.49583L10.2154 7.20229C10.0691 7.22851 9.96183 7.36463 9.96183 7.52409V10.6053C9.92691 10.6146 9.89128 10.624 9.8539 10.6322C9.63167 10.6825 9.36889 10.713 9.08811 10.713C8.83309 10.713 8.59252 10.6879 8.38406 10.6455C8.32409 10.6333 8.26766 10.6193 8.21369 10.6049V7.52445C8.21369 7.36499 8.10646 7.22887 7.96008 7.20265L4.24898 6.49619C4.06344 6.46279 3.89484 6.61615 3.89484 6.81799V14.5466C3.89484 14.7061 4.00207 14.8422 4.14845 14.8684L7.85955 15.5749C8.04508 15.6083 8.21369 15.4549 8.21369 15.2531V12.5508C8.4927 12.5961 8.78547 12.6194 9.08811 12.6198C9.3904 12.6198 9.68282 12.5957 9.96148 12.5508V15.2535C9.96148 15.4553 10.1304 15.6087 10.316 15.5753L14.0278 14.8688C14.1742 14.8426 14.2814 14.7065 14.2814 14.547V6.81835C14.2814 6.61651 14.1124 6.46315 13.9269 6.49655L13.9272 6.49583Z"
                        fill="#fff"
                    />
                    <defs>
                        <linearGradient
                            id="ghShieldCreateListLink"
                            x1="6.4916"
                            y1="1.83929"
                            x2="17.1247"
                            y2="25.6074"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stop-color="#FFB545" />
                            <stop offset="1" stop-color="#DB7712" />
                        </linearGradient>
                    </defs>
                </svg>
                <span class="underline">What are Expert Lists?</span>
            </button>

            <!-- List name -->
            <div class="mt-7">
                <label :class="labelClasses" for="create-list-name"
                    >{{ listMoniker }} List Name:</label
                >
                <input
                    id="create-list-name"
                    v-model="form.name"
                    type="text"
                    autocomplete="off"
                    :class="inputClasses"
                    @keyup.enter="submit"
                />
                <p
                    v-if="errorFor('name')"
                    class="mt-2 text-[13px] font-semibold text-brand-red"
                >
                    {{ errorFor("name") }}
                </p>
            </div>

            <!-- Privacy -->
            <div v-click-outside="() => (privacyOpen = false)" class="mt-6">
                <span :class="labelClasses">Who Can See This List?</span>
                <div class="relative">
                    <button
                        type="button"
                        class="flex h-11 w-full items-center gap-2.5 rounded-[5px] border border-solid border-[#cdc4c2] bg-white px-4 text-left transition focus:border-brand-navy focus:outline-none"
                        aria-haspopup="listbox"
                        :aria-expanded="privacyOpen"
                        @click="privacyOpen = !privacyOpen"
                    >
                        <svg
                            viewBox="0 0 16 16"
                            class="size-[13px] shrink-0 text-[#1d2333]"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.3"
                            aria-hidden="true"
                        >
                            <circle cx="8" cy="8" r="7" />
                            <path
                                d="M1 8h14M8 1c2.2 2 3.2 4.4 3.2 7S10.2 13 8 15c-2.2-2-3.2-4.4-3.2-7S5.8 3 8 1z"
                            />
                        </svg>
                        <span class="truncate text-[14px] text-[#1d2333]">
                            <span class="font-semibold">{{
                                selectedPrivacy.label
                            }}</span>
                            <span class="ml-2 font-normal">{{
                                selectedPrivacy.description
                            }}</span>
                        </span>
                        <svg
                            viewBox="0 0 13 10"
                            class="ml-auto h-2.5 w-[13px] shrink-0 text-brand-red transition"
                            :class="privacyOpen ? 'rotate-180' : ''"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M6.5 10L0 0h13L6.5 10z" />
                        </svg>
                    </button>
                    <ul
                        v-if="privacyOpen"
                        class="absolute inset-x-0 top-full z-10 mt-1 overflow-hidden rounded-[5px] border border-solid border-[#cdc4c2] bg-white shadow-lg"
                        role="listbox"
                    >
                        <li
                            v-for="option in privacyOptions"
                            :key="option.value"
                            role="option"
                            :aria-selected="option.value === form.privacy"
                        >
                            <button
                                type="button"
                                class="flex w-full items-baseline gap-2 px-4 py-3 text-left text-[14px] transition hover:bg-brand-navy/5"
                                :class="
                                    option.value === form.privacy
                                        ? 'bg-brand-navy/[0.03]'
                                        : ''
                                "
                                @click="selectPrivacy(option)"
                            >
                                <span class="font-semibold text-[#1d2333]">{{
                                    option.label
                                }}</span>
                                <span class="text-[#1d2333]/70">{{
                                    option.description
                                }}</span>
                            </button>
                        </li>
                    </ul>
                </div>
                <p
                    v-if="errorFor('privacy')"
                    class="mt-2 text-[13px] font-semibold text-brand-red"
                >
                    {{ errorFor("privacy") }}
                </p>
            </div>

            <!-- Event date + description (edit only, per Figma / GH-920) -->
            <template v-if="isEdit">
                <div class="mt-6">
                    <div class="flex items-baseline justify-between">
                        <span :class="labelClasses">Event Date</span>
                        <button
                            v-if="!isEventDateEmpty"
                            type="button"
                            class="text-[12px] font-semibold text-[#3c3938] underline transition hover:text-brand-navy"
                            @click="clearEventDate"
                        >
                            Clear
                        </button>
                    </div>
                    <div class="grid grid-cols-3 gap-4">
                        <div class="relative">
                            <select
                                v-model="eventMonth"
                                aria-label="Event month"
                                :class="eventSelectClasses"
                            >
                                <option :value="null">Month</option>
                                <option
                                    v-for="month in monthOptions"
                                    :key="month.value"
                                    :value="month.value"
                                >
                                    {{ month.label }}
                                </option>
                            </select>
                            <svg
                                viewBox="0 0 13 10"
                                :class="eventCaretClasses"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path d="M6.5 10L0 0h13L6.5 10z" />
                            </svg>
                        </div>
                        <div class="relative">
                            <select
                                v-model="eventDay"
                                aria-label="Event day"
                                :class="eventSelectClasses"
                            >
                                <option :value="null">Day</option>
                                <option
                                    v-for="day in dayOptions"
                                    :key="day"
                                    :value="day"
                                >
                                    {{ day }}
                                </option>
                            </select>
                            <svg
                                viewBox="0 0 13 10"
                                :class="eventCaretClasses"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path d="M6.5 10L0 0h13L6.5 10z" />
                            </svg>
                        </div>
                        <div class="relative">
                            <select
                                v-model="eventYear"
                                aria-label="Event year"
                                :class="eventSelectClasses"
                            >
                                <option :value="null">Year</option>
                                <option
                                    v-for="year in yearOptions"
                                    :key="year"
                                    :value="year"
                                >
                                    {{ year }}
                                </option>
                            </select>
                            <svg
                                viewBox="0 0 13 10"
                                :class="eventCaretClasses"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path d="M6.5 10L0 0h13L6.5 10z" />
                            </svg>
                        </div>
                    </div>
                    <p
                        v-if="errorFor('event_date')"
                        class="mt-2 text-[13px] font-semibold text-brand-red"
                    >
                        {{ errorFor("event_date") }}
                    </p>
                </div>

                <div class="mt-6">
                    <label :class="labelClasses" for="edit-list-description"
                        >Event Description</label
                    >
                    <textarea
                        id="edit-list-description"
                        v-model="form.description"
                        rows="4"
                        placeholder="A list of gift for&hellip;."
                        class="w-full rounded-[5px] border border-solid border-[#a79e9b] bg-white px-4 py-3 font-brand text-[15px] text-brand-navy placeholder:text-[#3c3938]/50 focus:border-brand-navy focus:outline-none"
                    ></textarea>
                    <p
                        v-if="errorFor('description')"
                        class="mt-2 text-[13px] font-semibold text-brand-red"
                    >
                        {{ errorFor("description") }}
                    </p>
                </div>
            </template>

            <!-- Privacy hint -->
            <div
                class="mt-7 flex items-center gap-3 rounded-[5px] border border-solid border-[#ffa100]/65 p-3.5"
            >
                <span
                    class="grid size-5 shrink-0 place-items-center rounded-full bg-[#ffa100] text-[13px] font-bold leading-none text-white"
                    aria-hidden="true"
                    >!</span
                >
                <p
                    class="text-[13px] font-semibold leading-[18px] text-[#3f3f3f]"
                >
                    Learn more about
                    <a
                        href="https://support.gifthero.com/page/managing-wish-lists#wish-list-privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="underline"
                        >Privacy Settings</a
                    >.
                </p>
            </div>

            <!-- Footer -->
            <div class="mt-9 flex items-center justify-between">
                <button
                    type="button"
                    class="text-[15px] font-bold text-brand-navy underline"
                    @click="modalogStore.closeModalog()"
                >
                    Cancel
                </button>
                <div class="flex items-center gap-4">
                    <span
                        v-if="errorFor('general')"
                        class="text-[13px] text-brand-red"
                        >{{ errorFor("general") }}</span
                    >
                    <button
                        type="button"
                        class="inline-flex h-[52px] min-w-[136px] items-center justify-center gap-2 rounded-brand bg-brand-navy px-6 text-[15px] font-bold text-white transition hover:bg-brand-navy/90 disabled:pointer-events-none"
                        :class="saving ? '' : 'disabled:opacity-30'"
                        :disabled="!canSubmit || saving"
                        @click="submit"
                    >
                        <template v-if="isEdit">{{
                            saving ? "Saving" : "Save"
                        }}</template>
                        <template v-else>{{
                            saving ? "Creating" : "Create List"
                        }}</template>
                        <BrandThrobber v-if="saving" />
                    </button>
                </div>
            </div>
        </BrandModalogShell>
    </div>
</template>
